import Fullscreen from '@mui/icons-material/Fullscreen'
import FullscreenExit from '@mui/icons-material/FullscreenExit'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { IconButton } from '@mui/material'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Carousel, Spinner } from 'react-bootstrap'
import QRCode from 'react-qr-code'
import ReactPlayer from 'react-player'
import { CADeviceUseFixingComponent } from 'src/clinic/components/ClinicHomePage/components/CADeviceUseFixingComponent'
import { StyledEventsRefreshIcon } from 'src/mentor/components/MentorEventsPage/MentorEventsPageStyles'
import { getImageLink, maskNameForClinicCounter } from 'src/mentor/helpers/utilityFunctions'
import {
  ClinicCarouselSlide,
  ClinicCarouselSlideImage,
  ClinicCarouselText,
  ClinicCarouselTextContainer,
  ClinicCounterAppointmentNumberBox,
  ClinicCounterBackground,
  ClinicCounterBody,
  ClinicCounterFooter,
  ClinicCounterHeader,
  ScreeningMentorAppointmentBox,
  StyledCarouselClinicCounter,
  TokenLabel,
  TokenPatientName,
  TokenValue,
} from '../../ClinicCounterStyles'
import {
  getPrefixRemovedAppointmentNumber,
  getScreenTitleForMentor,
  shouldShowYoutubeVideoForOOH,
} from '../../utils'
import { en, local } from '../../oohTranslations'
import OneMentorVideoAndOtherMentorCard from '../common/OneMentorVideoAndOtherMentorCard'

// Renders an English label with the configured local-language script below.
// Numbers (token values, names) must NEVER pass through this — they go in raw.
function Bilingual({ k, language, style }) {
  const localText = local(k, language)
  if (!localText) return <span style={style}>{en(k)}</span>
  return (
    <span style={{ ...style, display: 'inline-flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.15 }}>
      <span>{en(k)}</span>
      <span style={{ fontSize: '0.85em', opacity: 0.9 }}>{localText}</span>
    </span>
  )
}

// Persistent walk-in QR card. Themed to match the dark OOH palette so it
// doesn't read as a stark white card; the QR itself stays on a white inner
// panel for scan reliability. Two orientations: vertical (right column when
// only one practitioner) and horizontal (footer slot when a screener is also
// shown).
function WalkinQrCard({ url, language, orientation = 'vertical', expanded = false }) {
  if (!url) return null
  const isVertical = orientation === 'vertical'
  const headingScale = expanded ? 1.3 : 1.1
  const headingEn = en('scanAndBook')
  const headingLocal = local('scanAndBook', language)
  const subEn = en('scanThisQr')
  const subLocal = local('scanThisQr', language)

  // The title + sub-text are flex:0 0 auto (always reserve their space and
  // therefore always render); the QR fills the REMAINING space and shrinks to
  // fit, so it can never push the title out of an overflow:hidden card.
  const TextBlock = (
    <div style={{ textAlign: 'center', flex: '0 0 auto', minWidth: 0 }}>
      <div
        style={{
          color: '#e8b84a',
          fontWeight: 800,
          fontSize: isVertical
            ? `clamp(${18 * headingScale}px, ${1.8 * headingScale}vw, ${30 * headingScale}px)`
            : `clamp(${18 * headingScale}px, ${1.8 * headingScale}vw, ${30 * headingScale}px)`,
          lineHeight: 1.15,
          letterSpacing: '-0.01em',
        }}
      >
        {headingEn}
      </div>
      {headingLocal && (
        <div
          style={{
            marginTop: '2px',
            color: 'rgba(232, 184, 74, 0.85)',
            fontWeight: 600,
            fontSize: 'clamp(13px, 1.2vw, 20px)',
            lineHeight: 1.15,
          }}
        >
          {headingLocal}
        </div>
      )}
      <div
        style={{
          marginTop: '6px',
          color: 'rgba(255,255,255,0.62)',
          fontWeight: 500,
          fontSize: 'clamp(11px, 0.95vw, 15px)',
          lineHeight: 1.25,
        }}
      >
        <div>{subEn}</div>
        {subLocal && (
          <div style={{ fontSize: '0.92em', marginTop: '2px', opacity: 0.9 }}>{subLocal}</div>
        )}
      </div>
    </div>
  )

  const QrPanel = (
    // Flex-1 wrapper: takes the leftover space; the white square sizes to
    // "contain" within it (largest square that fits both width and height).
    <div
      style={{
        flex: '1 1 0',
        minHeight: 0,
        minWidth: 0,
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          background: '#fff',
          padding: 'clamp(6px, 0.6vw, 12px)',
          borderRadius: '16px',
          boxShadow: '0 10px 28px rgba(0,0,0,0.3)',
          aspectRatio: '1 / 1',
          height: '100%',
          maxHeight: '100%',
          maxWidth: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <QRCode value={url} style={{ width: '100%', height: '100%' }} viewBox="0 0 256 256" />
      </div>
    </div>
  )

  return (
    <div
      style={{
        margin: 'clamp(10px, 1.2vw, 16px)',
        height: 'auto',
        minHeight: 0,
        minWidth: 0,
        display: 'flex',
        flexDirection: isVertical ? 'column' : 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'clamp(10px, 1.4vw, 20px)',
        padding: 'clamp(14px, 1.6vw, 24px)',
        background: 'linear-gradient(160deg, #1a2332 0%, #243044 100%)',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        borderRadius: '20px',
        boxShadow: '0 16px 40px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.04) inset',
        overflow: 'hidden',
      }}
    >
      {TextBlock}
      {QrPanel}
    </div>
  )
}

// Footer token cell content laid out HORIZONTALLY to use the wide footer
// space: the big appointment number on the left, the label + patient name
// stacked on the right. Falls back to a vertical stack on narrow screens.
function FooterTokenContent({
  label,
  value,
  valueClassName,
  valueColor,
  slot,
  oohConfig,
  textColor,
  current = false,
}) {
  const lang = oohConfig?.displayLanguage
  const name = maskNameForClinicCounter(slot?.userName)
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'clamp(12px, 2vw, 32px)',
        minWidth: 0,
        minHeight: 0,
        width: '100%',
      }}
    >
      <TokenValue
        as="span"
        className={valueClassName}
        style={{
          color: valueColor,
          flex: '0 1 auto',
          fontSize: current
            ? 'min(clamp(56px, 10vw, 168px), 26vh)'
            : 'min(clamp(48px, 8vw, 132px), 22vh)',
        }}
      >
        {value}
      </TokenValue>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'center',
          textAlign: 'left',
          minWidth: 0,
          flex: '0 1 auto',
        }}
      >
        {label}
        <TokenPatientName as="div" style={{ color: textColor, textAlign: 'left', marginTop: '6px' }}>
          <div>
            {en('name')}: {name}
          </div>
          {local('name', lang) && (
            <div style={{ fontSize: '0.85em', opacity: 0.92 }}>
              {local('name', lang)}: {name}
            </div>
          )}
        </TokenPatientName>
      </div>
    </div>
  )
}

// Current + next token block rendered INSIDE a mentor card, so a practitioner's
// tokens sit with their identity (symmetric for both the left primary mentor and
// the right screener). `bookedSlots[0]` = current/now-serving, `[1]` = next.
function MentorTokenBlock({ bookedSlots, oohConfig, idPrefix = 'mentor' }) {
  const lang = oohConfig?.displayLanguage
  const nameOf = (slot) => maskNameForClinicCounter(slot?.userName)
  return (
    <>
      <ScreeningMentorAppointmentBox primary id={`${idPrefix}-curr-appointment`}>
        <TokenLabel
          as="div"
          style={{
            color: 'rgba(255,255,255,0.95)',
            marginBottom: '6px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <span
            style={{
              display: 'inline-block',
              width: '6px',
              height: '6px',
              borderRadius: '999px',
              background: '#22c55e',
              boxShadow: '0 0 0 3px rgba(34, 197, 94, 0.25)',
            }}
          />
          <Bilingual k="currentToken" language={lang} />
        </TokenLabel>
        <TokenValue as="span" style={{ color: '#fff', fontSize: 'clamp(40px, 4vw, 76px)' }}>
          {getPrefixRemovedAppointmentNumber(bookedSlots?.[0]?.appointmentNumber) || '--'}
        </TokenValue>
        <TokenPatientName
          as="div"
          style={{ color: 'rgba(255,255,255,0.92)', fontSize: 'clamp(12px, 1vw, 16px)', marginTop: '6px', textAlign: 'center' }}
        >
          <div>
            {en('name')}: {nameOf(bookedSlots?.[0])}
          </div>
          {local('name', lang) && (
            <div style={{ fontSize: '0.85em', opacity: 0.92 }}>
              {local('name', lang)}: {nameOf(bookedSlots?.[0])}
            </div>
          )}
        </TokenPatientName>
      </ScreeningMentorAppointmentBox>
      <ScreeningMentorAppointmentBox id={`${idPrefix}-next-appointment`}>
        <TokenLabel as="div" style={{ color: 'rgba(255,255,255,0.72)', marginBottom: '6px', textAlign: 'center' }}>
          <Bilingual k="nextToken" language={lang} />
        </TokenLabel>
        <TokenValue as="span" style={{ color: 'rgba(255,255,255,0.92)', fontSize: 'clamp(30px, 3vw, 56px)' }}>
          {getPrefixRemovedAppointmentNumber(bookedSlots?.[1]?.appointmentNumber) || '--'}
        </TokenValue>
        <TokenPatientName
          as="div"
          style={{ color: 'rgba(255,255,255,0.72)', fontSize: 'clamp(12px, 1vw, 16px)', marginTop: '6px', textAlign: 'center' }}
        >
          <div>
            {en('name')}: {nameOf(bookedSlots?.[1])}
          </div>
          {local('name', lang) && (
            <div style={{ fontSize: '0.85em', opacity: 0.92 }}>
              {local('name', lang)}: {nameOf(bookedSlots?.[1])}
            </div>
          )}
        </TokenPatientName>
      </ScreeningMentorAppointmentBox>
    </>
  )
}

export default function OneMentorVideoAndOther({
  thisClinicData,
  clinicBrandData,
  mentorProfileData,
  primaryMentorCheckedIn,
  OOHData,
  bookedSlotsInDay,
  bookedSlotsInDayLoading,
  screeningMentorDataLoading,
  screeningMentors,
  handleRefreshBookedSlots,
  handleExitFullScreen,
  handleClickFullScreen,
  timeNow,
  screenfull,
  oohConfig,
  walkinQr,
}) {
  const showScreeningMentor = !!screeningMentors && screeningMentors?.length > 0
  const hasOOHContent = Array.isArray(OOHData) && OOHData.length > 0
  const hasWalkinQr = !!walkinQr?.url
  // The center carousel only earns its space when there's actual rotating
  // content. With no OOH messages the carousel would otherwise be empty (or
  // pointlessly rotating one QR slide) — instead let the practitioner card
  // (and the persistent QR) fill the body.
  const showCarousel = hasOOHContent
  // Persistent QR gets its own always-on body column when:
  //  - there's no carousel to host it as a rotating slide, OR
  //  - the screen is configured to keep the QR always visible (pinned), so it
  //    never scrolls out with the carousel rotation.
  const qrAlwaysVisible = !!oohConfig?.walkinQrAlwaysVisible
  const showQrAsBodyColumn = hasWalkinQr && (qrAlwaysVisible || !showCarousel)
  // When the QR is pinned as its own column, don't ALSO rotate it in the
  // carousel (would be redundant); otherwise it falls back to a rotating slide.
  const showQrInCarousel = hasWalkinQr && !showQrAsBodyColumn
  // Two practitioners share the screen symmetrically: each card carries its
  // OWN tokens inside it, and the bottom footer is dropped entirely (it caused
  // the asymmetry + clipped/overlapped token cards). The big footer token
  // strip is reserved for the single-practitioner layout.
  const showFooterTokens = !showScreeningMentor
  // "Hero" expansion only when a card is identity-only (no in-card tokens) and
  // there's no carousel competing for space.
  const expandedCards = !showCarousel

  // Build the body grid column template to exactly match the rendered cells,
  // in render order: primary, [carousel], [screener], [QR].
  const bodyCols = []
  bodyCols.push(showCarousel ? '0.26fr' : '1fr') // primary
  if (showCarousel) bodyCols.push('1fr') // carousel is the dominant center
  if (showScreeningMentor) bodyCols.push(showCarousel ? '0.26fr' : '1fr') // screener
  if (showQrAsBodyColumn) bodyCols.push(showCarousel ? '0.5fr' : '1fr') // persistent QR
  const bodyGridTemplate = bodyCols.join(' ')

  const [currentlyShowingScreenerMentor, setCurrentlyShowingScreenerMentor] = useState(0)

  const [CAFlowDialogOpen, setCAFlowDialogOpen] = useState(false)


  //iterate through the screeningMentors and one by one just put in currentlyShowingScreenerMentor. if the item is last, return to first. delay: 45 seconds
  useEffect(() => {
    if (showScreeningMentor) {
      const interval = setInterval(() => {
        setCurrentlyShowingScreenerMentor((prevIndex) => (prevIndex + 1) % screeningMentors.length)
      }, 45000)

      // Clear interval on component unmount
      return () => clearInterval(interval)
    }
  }, [screeningMentors, showScreeningMentor])

  const [anchorElForMenu, setAnchorElForMenu] = useState(null)
  const menuOpen = Boolean(anchorElForMenu)
  const handleClickMenuOpener = (event) => {
    setAnchorElForMenu(event.currentTarget)
  }
  const handleCloseMenu = () => {
    setAnchorElForMenu(null)
  }

  return (
    <ClinicCounterBackground id="fullScreenTarget">
      <ClinicCounterHeader>
        <div
          style={{
            position: 'absolute',
            left: '24px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            lineHeight: 1.15,
          }}
        >
          <div
            style={{
              fontSize: 'clamp(11px, 0.95vw, 14px)',
              fontWeight: 500,
              color: 'rgba(255,255,255,0.6)',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
            }}
          >
            {timeNow.format('ddd, MMM D')}
          </div>
          <div
            style={{
              fontSize: 'clamp(18px, 2vw, 30px)',
              fontWeight: 700,
              color: 'rgba(255,255,255,0.96)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {timeNow.format('h:mm A')}
          </div>
        </div>
        <span
          style={{
            display: 'inline-flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            maxWidth: 'min(60vw, 820px)',
            textAlign: 'center',
            lineHeight: 1.15,
          }}
        >
          {(clinicBrandData?.clinicBrandName || clinicBrandData?.name) && (
            <span
              style={{
                fontSize: 'clamp(14px, 1.4vw, 22px)',
                fontWeight: 700,
                color: '#e8b84a',
                letterSpacing: '0.01em',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
              }}
            >
              {clinicBrandData?.clinicBrandName || clinicBrandData?.name}
            </span>
          )}
          <span
            style={{
              fontSize: 'clamp(11px, 1.05vw, 16px)',
              fontWeight: 500,
              color: 'rgba(255,255,255,0.72)',
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%',
              marginTop: '2px',
            }}
          >
            {thisClinicData?.title || thisClinicData?.name}
          </span>
        </span>
        <div
          style={{
            position: 'absolute',
            right: '16px',
            display: 'flex',
            gap: '8px',
            flexDirection: 'row-reverse',
            alignItems: 'center',
          }}
        >
          <IconButton id="demo-positioned-button-counter" onClick={handleClickMenuOpener}>
            <MoreVertIcon style={{ color: 'rgba(255,255,255,0.9)' }} />
          </IconButton>
          {bookedSlotsInDayLoading ? (
            <Spinner animation="border" variant="light" size="sm" />
          ) : (
            <StyledEventsRefreshIcon onClick={handleRefreshBookedSlots} />
          )}
          {screenfull.isEnabled &&
            (screenfull.isFullscreen ? (
              <FullscreenExit style={{ cursor: 'pointer' }} onClick={handleExitFullScreen} />
            ) : (
              <Fullscreen style={{ cursor: 'pointer' }} onClick={handleClickFullScreen} />
            ))}
          {/* <Close style={{ cursor: 'pointer' }} onClick={() => window.history.back()} /> */}
          <MenuComponent
            anchorElForMenu={anchorElForMenu}
            menuOpen={menuOpen}
            handleCloseMenu={handleCloseMenu}
            openDeviceUseSettings={() => setCAFlowDialogOpen(true)}
          />
          <CADeviceUseFixingComponent
            CAFlowDialogOpen={CAFlowDialogOpen}
            setsCAFlowDialogOpen={setCAFlowDialogOpen}
          />
        </div>
      </ClinicCounterHeader>

      <ClinicCounterBody
        singleMentor={!showScreeningMentor && !walkinQr?.url}
        gridTemplate={bodyGridTemplate}
      >
        <OneMentorVideoAndOtherMentorCard
          loading={screeningMentorDataLoading}
          mentor={mentorProfileData}
          mentorTitle={getScreenTitleForMentor(mentorProfileData)}
          checkedIn={primaryMentorCheckedIn}
          hideMentorDetails={oohConfig?.hideMentorDetails}
          displayLanguage={oohConfig?.displayLanguage}
          expanded={expandedCards}
        >
          {/* Two-mentor mode: the left mentor's tokens live INSIDE this card,
              symmetric to the screener card on the right. Single-mentor mode
              keeps the big footer token strip instead. */}
          {showScreeningMentor && (
            <MentorTokenBlock
              bookedSlots={bookedSlotsInDay}
              oohConfig={oohConfig}
              idPrefix="primary-mentor"
            />
          )}
        </OneMentorVideoAndOtherMentorCard>

        {showCarousel && (
        <StyledCarouselClinicCounter pause={false}>
          {showQrInCarousel && (
            <Carousel.Item interval={15000} key="walkin-qr">
              <ClinicCarouselSlide>
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background:
                      'radial-gradient(circle at 50% 0%, #243044 0%, #1a2332 60%, #0f1419 100%)',
                    gap: '20px',
                    padding: 'clamp(20px, 2.5vw, 40px)',
                  }}
                >
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: 'clamp(28px, 3vw, 48px)',
                      color: '#e8b84a',
                      textAlign: 'center',
                      lineHeight: 1.15,
                      letterSpacing: '-0.005em',
                    }}
                  >
                    <div>{en('scanAndBook')}</div>
                    {local('scanAndBook', oohConfig?.displayLanguage) && (
                      <div
                        style={{
                          fontSize: '0.78em',
                          opacity: 0.85,
                          color: 'rgba(232, 184, 74, 0.9)',
                          marginTop: '4px',
                        }}
                      >
                        {local('scanAndBook', oohConfig?.displayLanguage)}
                      </div>
                    )}
                  </div>
                  <div
                    style={{
                      background: '#fff',
                      padding: 'clamp(12px, 1.2vw, 20px)',
                      borderRadius: '20px',
                      boxShadow:
                        '0 24px 56px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.05)',
                      maxWidth: '85vmin',
                      width: 'min(70vh, 85vw)',
                      aspectRatio: '1 / 1',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <QRCode
                      value={walkinQr.url}
                      style={{ width: '100%', height: '100%' }}
                      viewBox="0 0 256 256"
                    />
                  </div>
                  <div
                    style={{
                      fontSize: 'clamp(16px, 1.6vw, 22px)',
                      color: 'rgba(255,255,255,0.7)',
                      textAlign: 'center',
                      maxWidth: '620px',
                      lineHeight: 1.3,
                    }}
                  >
                    <div>{en('scanThisQr')}</div>
                    {local('scanThisQr', oohConfig?.displayLanguage) && (
                      <div style={{ fontSize: '0.92em', opacity: 0.85, marginTop: '4px' }}>
                        {local('scanThisQr', oohConfig?.displayLanguage)}
                      </div>
                    )}
                  </div>
                </div>
              </ClinicCarouselSlide>
            </Carousel.Item>
          )}
          {OOHData?.map((carouselDataItem, carouselDataItemIndex) => {
            if (carouselDataItem?.imageType === 'image') {
              return (
                <Carousel.Item
                  interval={carouselDataItem?.durationInSec * 1000 || 5000}
                  key={carouselDataItemIndex}
                >
                  <ClinicCarouselSlide>
                    <ClinicCarouselSlideImage
                      src={getImageLink(carouselDataItem?.imageId)}
                      blackTint={
                        carouselDataItem?.title?.trim() || carouselDataItem?.subText?.trim()
                      }
                    />
                    <ClinicCarouselTextContainer>
                      <ClinicCarouselText main>{carouselDataItem?.title}</ClinicCarouselText>
                      <ClinicCarouselText>{carouselDataItem?.subText}</ClinicCarouselText>
                    </ClinicCarouselTextContainer>
                  </ClinicCarouselSlide>
                </Carousel.Item>
              )
            } else if (
              carouselDataItem?.imageType === 'youtube_video' &&
              shouldShowYoutubeVideoForOOH(thisClinicData)
            ) {
              return (
                <Carousel.Item
                  interval={carouselDataItem?.durationInSec * 1000 || 45000}
                  key={carouselDataItemIndex}
                >
                  <ClinicCarouselSlide>
                    <ReactPlayer
                      url={carouselDataItem?.youtubeUrl || carouselDataItem?.imageUrl}
                      config={{
                        playerVars: {
                          cc_lang_pref: 'hi',
                          cc_load_policy: 1,
                        },
                      }}
                      playing={true}
                      width="100%"
                      height="100%"
                      muted={true}
                    />
                  </ClinicCarouselSlide>
                </Carousel.Item>
              )
            }
            return null
          })}
        </StyledCarouselClinicCounter>
        )}

        {showScreeningMentor && (
          <ScreenerProfileCard
            screeningMentorDataLoading={screeningMentorDataLoading}
            currentlyShowingScreenerMentor={screeningMentors?.[currentlyShowingScreenerMentor]}
            bookedSlotsForScreenerInDay={
              screeningMentors?.[currentlyShowingScreenerMentor]?.bookedSlots
            }
            oohConfig={oohConfig}
            expanded={expandedCards}
          />
        )}
        {showQrAsBodyColumn && (
          <WalkinQrCard
            url={walkinQr.url}
            language={oohConfig?.displayLanguage}
            orientation="vertical"
            expanded={expandedCards && !showScreeningMentor}
          />
        )}
      </ClinicCounterBody>

      {showFooterTokens && (
      <ClinicCounterFooter>
        <ClinicCounterAppointmentNumberBox main>
          <FooterTokenContent
            current
            label={
              <TokenLabel
                as="div"
                style={{
                  color: 'rgba(255,255,255,0.95)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: 0,
                }}
              >
                <span
                  style={{
                    display: 'inline-block',
                    width: 'clamp(7px, 0.7vw, 11px)',
                    height: 'clamp(7px, 0.7vw, 11px)',
                    borderRadius: '999px',
                    background: '#22c55e',
                    boxShadow: '0 0 0 4px rgba(34, 197, 94, 0.25)',
                    flexShrink: 0,
                  }}
                />
                <Bilingual k="currentToken" language={oohConfig?.displayLanguage} />
              </TokenLabel>
            }
            value={getPrefixRemovedAppointmentNumber(bookedSlotsInDay?.[0]?.appointmentNumber) || '--'}
            valueClassName="current-token"
            valueColor="#fff"
            slot={bookedSlotsInDay?.[0]}
            oohConfig={oohConfig}
            textColor="rgba(255,255,255,0.92)"
          />
        </ClinicCounterAppointmentNumberBox>
        <ClinicCounterAppointmentNumberBox>
          <FooterTokenContent
            label={
              <TokenLabel as="div" style={{ color: 'rgba(255,255,255,0.72)', marginBottom: 0 }}>
                <Bilingual k="nextToken" language={oohConfig?.displayLanguage} />
              </TokenLabel>
            }
            value={getPrefixRemovedAppointmentNumber(bookedSlotsInDay?.[1]?.appointmentNumber) || '--'}
            valueColor="rgba(255,255,255,0.92)"
            slot={bookedSlotsInDay?.[1]}
            oohConfig={oohConfig}
            textColor="rgba(255,255,255,0.72)"
          />
        </ClinicCounterAppointmentNumberBox>
      </ClinicCounterFooter>
      )}
    </ClinicCounterBackground>
  )
}

function ScreenerProfileCard({
  screeningMentorDataLoading,
  currentlyShowingScreenerMentor,
  bookedSlotsForScreenerInDay,
  oohConfig,
  expanded = false,
}) {
  return (
    <OneMentorVideoAndOtherMentorCard
      loading={screeningMentorDataLoading}
      mentor={currentlyShowingScreenerMentor}
      mentorTitle={getScreenTitleForMentor(currentlyShowingScreenerMentor)}
      checkedIn={true}
      hideMentorDetails={oohConfig?.hideMentorDetails}
      displayLanguage={oohConfig?.displayLanguage}
      expanded={expanded}
      isRightColumn
    >
      <MentorTokenBlock
        bookedSlots={bookedSlotsForScreenerInDay}
        oohConfig={oohConfig}
        idPrefix="screening-mentor"
      />
    </OneMentorVideoAndOtherMentorCard>
  )
}

const MenuComponent = ({ anchorElForMenu, menuOpen, handleCloseMenu, openDeviceUseSettings }) => {
  const navigate = useNavigate()
  const currentPath = window.location.pathname
  const isDisplayPortal = currentPath.startsWith('/display')
  const isKioskPortal = currentPath.startsWith('/kiosk') || isDisplayPortal
  const selectFlowPath = isDisplayPortal ? '/display/select-flow' : '/kiosk/select-flow'

  return (
    <Menu
      id="demo-positioned-menu"
      aria-labelledby="demo-positioned-button"
      anchorEl={anchorElForMenu}
      open={menuOpen}
      onClose={handleCloseMenu}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
    >
      {isKioskPortal ? (
        <MenuItem
          onClick={() => {
            navigate(selectFlowPath)
            handleCloseMenu()
          }}
        >
          Change Flow
        </MenuItem>
      ) : (
        <MenuItem
          onClick={() => {
            openDeviceUseSettings()
            handleCloseMenu()
          }}
        >
          Change Device-use Settings
        </MenuItem>
      )}
    </Menu>
  )
}
